/*
  this mixin includes all methods that request data from the API

  i designed the page for async/await, because i know that from the .NET CLR :)

  therefore, it would not make sense to convert all methods to promise chains (and recreate the page logic),
  as it is just a wrapper that makes promises easier to read and use
  --> https://developer.mozilla.org/en-US/docs/Learn/JavaScript/Asynchronous/Promises#async_and_await

  i have added promise-chains to some methods before (you can find them in the "unused methods" region),
  but then i changed them back, because the code looks very messy
  --> a LOT of nestings
*/
// api
import {Api} from "@/api/apiAccess.ts";
import {Semaphore} from "@/assets/Semaphore.js";

// vuex
import {Mutators, GET_SESSIONDATA, UPDATE_AUTHTOKEN} from "@/store/storeMutator.js";
import {mapGetters} from "vuex";

// mixins
import HelpersMixin from "@/mixins/helpers.js";
import Constants from "@/mixins/constants.js";
import Tags from "@/mixins/tags.js";

export class SessionData {
  /**
   * respresents the current session
   *
   * stores all data needed for the page to work
   *
   * @param {String} authToken the authtoken from the loginresult
   * @param {Object} memberPerissions permissions of the current user
   * @param {Club} club the current club
   */
  constructor(member, club) {
    this.member = member;
    this.club = club;
  }
}

export class Member {
  /**
   * represents a member
   * @param {*} addressNumber id from the clubs app account
   * @param {*} authToken bearer token
   * @param {*} lastLoginId
   * @param {*} permissions
   * @param {*} name
   * @param {*} photo
   * @memberof Member
   */
  constructor(addressNumber, authToken, lastLoginId, permissions, name, photo) {
    this.addressNumber = addressNumber;
    this.authToken = authToken;
    this.lastLoginId = lastLoginId;
    this.permissions = permissions;
    this.name = name;
    this.photo = photo;
  }
}

export class Club {
  /**
   * represents the club for the current session
   * @param {String} clubId id of the club
   * @param {String} config club's configuration
   * @param {String} name name of the club
   * @param {String} homepage homepage-url of the club
   * @param {String} telephone clubs telephone number
   * @param {String} street street of the club
   * @param {String} city city of the club
   * @param {Number} zip zip code of the club
   * @param {String} logo (base64) logo of the club
   * @param {Object} courseConfig the current course configuration of the club
   * @param {Array<Object>} halls all existing/active halls of the club
   * @param {Array<Object>} courses all existing/active courses of the club
   */
  constructor(clubId, config, name, homepage, telephone, street, city, zip, logo, courseConfig, halls, courses) {
    this.clubId = clubId;
    this.config = config;
    this.homepage = homepage;
    this.telephone = telephone;
    this.zip = zip;
    this.city = city;
    this.street = street;
    this.name = name;
    this.logo = logo;
    this.courseConfig = courseConfig;
    this.halls = halls;
    this.courses = courses;
  }
}

export default {
  mixins: [HelpersMixin, Tags, Constants],

  data: () => {
    return {
      additionalInfoSema: new Semaphore(1),
    };
  },

  methods: {
    /**
     * Resets the SessionData so that it is possible to log in again
     */
    reset() {
      Mutators.updateSessionData({});
    },

    /**
     * loads all data needed for the display of the PreviewItems
     */
    async loadSessionData() {
      // load courses
      var courses = await this.prepareCourses();
      // load halls
      var halls = await this.prepareHalls();
      // load course config
      var courseConfig = (await Api.HaKuBu.haKuBuGetClubCourseConfig(this.SessionData.club.clubId)).data[0]

      if (courseConfig === null || courseConfig === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return;
      }

      var sessionData = this.SessionData;

      // add them to session data
      sessionData.club.courses = courses;
      sessionData.club.halls = halls;
      sessionData.club.courseConfig = courseConfig;

      Mutators.updateSessionData(sessionData);
    },

    //#region login/register
    /**
     * logs the user in and then loads courses, halls and the configuration
     *
     * handles errors and shows corresponding snackbars
     *
     * @param {Object} AppLoginViewModel login credentials
     * @param {String} clubId id of the club to be loaded (clubId from route parameter)
     *
     * @returns {Number} 0   success
     * @returns {Number} -1   password or addressnumber was null
     * @returns {Number} -2   club db could not be determined
     * @returns {Number} -3   addressnumber was not found in the club
     * @returns {Number} -4   wrong password
     * @returns {Number} -5   unknown error occured
     * @returns {Number} -6   permissions could not be loaded
     * @returns {Number} -7   clubmember has insufficient permissions
     * @returns {Number} -8   club data could not be loaded
     *
     */
    async loginUser(PersonenLoginViewModel, clubId) {
      var loginResult = (await Api.HaKuBu.haKuBuPersonenLogin(PersonenLoginViewModel)).data;

      if (loginResult === null || loginResult === undefined || loginResult.ModelError !== null) return -5;
      if (loginResult.LoginId < 0) return loginResult.LoginId;

      // create clubmember and update authToken store, without the permissions (we need the AuthToken in the store)
      var memberNoPerms = new Member(loginResult.LoginId, loginResult.LoginToken, loginResult.LastLoginId, null);
      Mutators.updateMember(memberNoPerms);
      
      
      // perform ClubLogin to receive permissions
      var permissionResult = (await Api.HaKuBu.haKuBuGetPermissions(PersonenLoginViewModel.VereinsNummer, PersonenLoginViewModel.AddressNummer)).data;
      if (permissionResult === undefined || permissionResult === null) return -6;

      // check for permissions
      if (!permissionResult.KurseLesen && !permissionResult.HallenLesen) return -7;

      // get personal data
      var personalData = (await Api.HaKuBu.haKuBuLoadPersonalData(clubId, true, 100, 100)).data;
      var memberFullName = "";

      // set title
      if (personalData.Title) memberFullName = personalData.Title + " ";
      if (personalData.FirstName && personalData.LastName) memberFullName = memberFullName + personalData.FirstName + " " + personalData.LastName;  

      // create clubmember and update store, this time with the permissions
      var memberWithPerms = new Member(loginResult.LoginId,
        loginResult.LoginToken,
        loginResult.LastLoginId,
        permissionResult,
        memberFullName,
        personalData.Photo);
      Mutators.updateMember(memberWithPerms);

      // load club data
      var clubData = (await Api.HaKuBu.haKuBuGetClubData(500, 500, clubId)).data;
      if (clubData === undefined || clubData === null) return -8;

      // seperate zipcode and city from response
      let zipCitySplit = clubData.Vereins_PlzOrt.split(" ");
      let lastIndex = zipCitySplit.length - 1;
      let zip = zipCitySplit[0];
      let city = zipCitySplit[lastIndex];

      var configData = (await Api.HaKuBu.haKuBuGetConfig(this.$route.params.clubId)).data;
      if (configData === undefined || configData === null) return -8;

      // create club
      var club = new Club(clubId, configData, clubData.Vereins_Name,
                          clubData.VER_HOMEPAGE, clubData.VER_TEL, clubData.Vereins_Strasse,
                          city, zip, clubData.Logo, null, null, null);

      // create sessiondata
      var sessionData = new SessionData(memberWithPerms, club);
      Mutators.updateSessionData(sessionData);

      await this.loadSessionData(clubId);

      return 0;
    },
    // #endregion

    // #region courses
    /**
     * loads all courses available in the current club and prepares them for displaying on the ui
     *
     * @return {Array[Object]} available courses with booked and reservated prop
     */
    async prepareCourses() {
      var availableCourses = (await Api.HaKuBu.haKuBuGetClubCourses(false, 0, this.SessionData.club.clubId, 0, 500, 500)).data;
      var bookedCourses = (await Api.HaKuBu.haKuBuGetPersonalCourses(0, this.SessionData.club.clubId, 500, 500)).data;

      // error(code) handling
      if (availableCourses === null || availableCourses === undefined || bookedCourses === null || bookedCourses === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return null;
      }

      // filter out inactive courses
      availableCourses = availableCourses.filter((availCourse) => new Date(availCourse.CourseEndDate) > Date.now());

      var coursesWithAdditionalProps = await this.addAdditionalPropsToCourse(availableCourses, bookedCourses);

      return coursesWithAdditionalProps;
    },

    /**
     * adds the "booked" and "reserved" props to courses
     *
     * @param {*} courses courses that should get booked and reserverd props
     * @param {*} bookedCourses booked courses
     * @returns courses with booked and reserved props
     */
    addAdditionalPropsToCourse(courses, bookedCourses) {
      // get course ids from booked courses
      var bookedCourseIds = bookedCourses.map((bookedCourse) => bookedCourse.CourseId);

      return courses.filter((availCourse) => {
        if (bookedCourseIds.some((persCourseId) => persCourseId === availCourse.CourseId)) {
          var bookedCourse = bookedCourses.filter((bookedCourse) => bookedCourse.CourseId == availCourse.CourseId)[0];

          availCourse.bookingInfo = bookedCourse;
          availCourse.reserved = bookedCourse.CourseIsReserved;

          availCourse.booked = !bookedCourse.CourseIsReserved && !bookedCourse.CourseIsPayed && !bookedCourse.CourseIsInvoiced;
          availCourse.payed = bookedCourse.CourseIsPayed || bookedCourse.CourseIsInvoiced;

        } else {
          availCourse.booked = false;
          availCourse.reserved = false;
          availCourse.payed = false;
        }

        availCourse.tags = this.getCourseTags(availCourse);

        return availCourse;
      });
    },

    /**
     * loads required information about the course
     * @returns 0 - success
     * @returns -1 - course is undefined
     */
    async loadAdditionalCourseInfo(course, forceLoad = false) {
      await this.additionalInfoSema.lock();

      if (course === undefined) {
        this.additionalInfoSema.release();
        return -1;
      }

      var courseObjKeys = Object.keys(course);

      // details
      if (forceLoad || !courseObjKeys.includes("details")) {
        var details = (await Api.HaKuBu.haKuBuGetCourseDetails(this.SessionData.club.clubId, course.CourseId, true, 250, 250)).data;

        // error handling
        if (details === null || details === undefined) {
          this.$root.snackbar.show(this.$root.snackbars.genericError);
          return;
        }

        course.details = details;
      }

      // additional details
      if (forceLoad || !courseObjKeys.includes("additionalDetails")) {
        var additionalDetails = (await Api.HaKuBu.haKuBuGetClubCourseAdditionalDetails(this.SessionData.club.clubId,
                                                                                      course.CourseId, 
                                                                                      true, 
                                                                                      250, 
                                                                                      250)).data;

        // error handling
        if (additionalDetails === null || additionalDetails === undefined) {
          this.$root.snackbar.show(this.$root.snackbars.genericError);
          return;
        }

        course.additionalDetails = additionalDetails;
      }

      // meetings
      if (forceLoad || !courseObjKeys.includes("meetings")) {
        var meetings = (await Api.HaKuBu.haKuBuGetCourseMeetings(this.SessionData.club.clubId, course.CourseId, true, false)).data;

        // error handling
        if (meetings === null || meetings === undefined) {
          this.$root.snackbar.show(this.$root.snackbars.genericError);
          return;
        }

        var futureMeetings = meetings.filter((meeting) => new Date(meeting.MeetingDate) > Date.now());
        course.meetings = futureMeetings;
      }

      Mutators.updateCourse(course);

      this.additionalInfoSema.release();

      return new Promise((resolve) => setTimeout(() => resolve()), 1);
    },

    /** updates a course after booking or reservating */
    async updateCourse(courseIdToUpdate) {
      var availableCourses = (await Api.HaKuBu.haKuBuGetClubCourses(false, 0, this.SessionData.club.clubId, 0, 500, 500)).data;
      var bookedCourses = (await Api.HaKuBu.haKuBuGetPersonalCourses(0, this.SessionData.club.clubId, 500, 500)).data;

      // error handling
      if (availableCourses === null || availableCourses === undefined || bookedCourses === null || bookedCourses === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return;
      }

      // get course that should be updated
      var courseToBeUpdated = availableCourses.filter((availCourse) => availCourse.CourseId == courseIdToUpdate)[0];

      var updatedCourse = (await this.addAdditionalPropsToCourse([courseToBeUpdated], bookedCourses))[0];

      await this.loadAdditionalCourseInfo(updatedCourse, true);
    },
    // #endregion

    // #region halls
    /**
     * loads all halls available in the current club and prepares them for displaying on the ui
     * @returns {Array[Object]} available halls with booked prop
     */
    async prepareHalls() {
      var availableHalls = (await Api.HaKuBu.haKuBuGetHalls(0, this.SessionData.club.clubId, 0)).data;
      var bookedHalls = (await Api.HaKuBu.haKuBuGetNextHallMeetings(0, this.SessionData.club.clubId)).data;

      // error handling
      if (availableHalls === null || availableHalls === undefined || bookedHalls === null || bookedHalls === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return;
      }

      var hallsWithAdditionalProps = await this.addAdditionalPropsToHall(availableHalls, bookedHalls);

      return hallsWithAdditionalProps;
    },

    /**
     * adds the "booked", "reserved" and "details" props to courses
     * changes the order of the open days: monday and sunday are changed
     *
     * @param {*} halls halls that should get booked and reserverd props
     * @param {*} bookedHalls booked halls
     * @returns halls with booked and reserved props
     */
    async addAdditionalPropsToHall(halls, bookedHalls) {
      // get hall ids from booked halls
      var bookedHallIds = bookedHalls.map((bookedHall) => bookedHall.HallId);

      return await this.filterAsync(halls, async (availHall) => {
        // get booked meetings for current hall
        var bookedMeetingsForCurrentHall = bookedHallIds.filter((bookedHallId) => bookedHallId === availHall.Id);

        // add booked prop to hall object
        availHall.booked = bookedMeetingsForCurrentHall.length > 0;

        availHall.tags = this.getHallTags(availHall);

        return availHall;
      });
    },

    /**
     * loads all hall details
     *
     * @param {*} hallId hall id to be updated
     * @returns {Number} -1 hallId was undefined
     * @returns {Number} -2 hall was not found
     * @returns {Number} -3 hall details could not be loaded
     */
    async loadHallDetails(hallId, forceLoad = false) {
      await this.additionalInfoSema.lock();

      if (!hallId || hallId === undefined || hallId === null) {
        this.additionalInfoSema.release();
        return -1;
      }

      var hallToUpdate = this.SessionData.club.halls.filter((hall) => hall.Id === hallId)[0];

      if (!hallId || hallId === undefined || hallId === null) {
        this.additionalInfoSema.release();
        return -2;
      }

      var courseObjKeys = Object.keys(hallToUpdate);

      // details
      if (forceLoad || !courseObjKeys.includes("details")) {
        var hallDetails = (await Api.HaKuBu.haKuBuGetHallDetails(this.SessionData.club.clubId, hallId, false)).data;

        if (hallDetails === undefined || hallDetails === null) return -3;

        // add hall details to hall object
        hallToUpdate.details = hallDetails;
        // change the order of opening hours
        // love those destructuring assignments: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Destructuring_assignment
        hallToUpdate.details.OpeningHours.push(hallToUpdate.details.OpeningHours.shift());
        Mutators.updateHall(hallToUpdate);
      }

      this.additionalInfoSema.release();
    },

    /** updates a hall after booking or reservating */
    async updateHall(hallIdToUpdate) {
      var availableHalls = (await Api.HaKuBu.haKuBuGetHalls(0, this.SessionData.club.clubId, 0)).data;
      var bookedHalls = (await Api.HaKuBu.haKuBuGetNextHallMeetings(0, this.SessionData.club.clubId)).data;

      // error handling
      if (availableHalls === null || availableHalls === undefined || bookedHalls === null || bookedHalls === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return;
      }

      // get hall that should be updated
      var hallToBeUpdated = availableHalls.filter((availHall) => availHall.Id == hallIdToUpdate)[0];

      var updatedHall = (await this.addAdditionalPropsToHall([hallToBeUpdated], bookedHalls))[0];

      Mutators.updateHall(updatedHall);

      this.loadHallDetails(updatedHall.Id);
    },
    // #endregion
  },

  computed: {
    ...mapGetters({SessionData: GET_SESSIONDATA}),
  },
};